import { Card } from "@/paths/games/gameboardTypes";

// Type lists
export const TriggerTypeList = [
    "targets", "damage", "ub-damage", "heal", "ritual", "create",
    "draw", "discard", "gain-control", "counter",
    "add-focus", "remove-focus", "tutor", "any"
] as const;

export const EffectTypeList = [
    "give-control", "gain-control", "store", "damage",
    "ub-damage", "heal", "draw", "discard", "ritual", "create",
    "counter", "add-focus", "remove-focus", "tutor"
] as const;
export const TargetCountTypeList = ["all"] as const;
export const AbilityTypeList = ["cast", "play", "sustain", "passive"] as const;
export const controllerOptionsList = ["self", "opponent", "target"] as const;
export const zoneOptionsList = ["hand", "discard", "battlefield", "deck", "all"] as const;
export const targetTypesList = ["player", "card", "class"] as const;
export const MathSignsList = ["*", "/", "+", "-", "^"] as const;
export const CardPropertyOptions = ["universalId", "cost", "castThreshold", "focusTotal", "focus", "focusTemp"] as const;
export const ModifierUpdateOptionsList = ["void", "amount"] as const;

// type lists as types
export type TargetTypes = (typeof targetTypesList)[number];
export type AbilityTypes = (typeof AbilityTypeList)[number];
export type ReadableProperty = (typeof CardPropertyOptions)[number];
export type TriggerType = typeof TriggerTypeList[number];
export type EffectType = typeof EffectTypeList[number];
export type TargetCount = typeof TargetCountTypeList[number] | number;
export type ControllerType = (typeof controllerOptionsList)[number];
export type Zone = (typeof zoneOptionsList)[number];
export type MathSign = (typeof MathSignsList)[number];

export interface ModifierValue {
    type: "modifier-value",
}

export interface TargetCountValue {
    type: "target-count";
    target: TargetType;
}

export interface MathValue {
    type: "math";
    sign: MathSign;
    left: Value;
    right: Value;
}

export interface PropertyValue {
    type: "property";
    property: ReadableProperty;
    cardId: "this" | StoredCardId;
}

export interface UserValue {
    type: "user";
    max: Value;
    min: Value;
}

export interface StoredValue {
    type: "stored-value";
    key: string;
}

export interface RitualValue {
    type: "ritual-value";
}

export interface StoredCardId {
    type: "stored-card";
    key: string;
}

export interface Target {
    type: TargetTypes
    controller?: ControllerType;
    id?: "this" | StoredCardId;
    idNot?: "this" | StoredCardId;
    zone?: Zone;
    count?: TargetCount;
}

// Direct effects
export interface DirectEffect {
    type: EffectType;
    amount?: number | Value;
    value?: string;
    by?: string;
    target: TargetType;
    secondaryTarget?: TargetType;
}

export interface MultipleEffect {
    type: "multiple";
    set: Effect[];
}

export interface IteratedEffect {
    type: "iterated";
    effect: Effect;
    count: Value;
}

export interface TriggerFrame {
    type: "trigger";
    condition: TriggerType;
    source?: AbilityTypes | "any" | "SYSTEM";
    on: TargetType;
    then?: ModifierEffect;
    and?: Effect;
}

export interface ModifierEffect {
    type: "modifier";
    update: "void" | "amount";
    effect?: Value;
}

// Main Effect union
export type TargetType = Target | StoredCardId
export type Effect = MultipleEffect | DirectEffect | IteratedEffect | TriggerFrame;
export type Value = RitualValue | TargetCountValue | PropertyValue | UserValue | MathValue | StoredValue | ModifierValue | number;

export type Ability = {
    playerId: string;
    effect: Effect;
    source: AbilityTypes;
    card: Card;
    count: number;
};



export const SCHOOL_NAMES = ['Manifest', 'Arcane', 'Thermal', 'Kinetic', 'Electric', 'Light', 'Spatial', 'Time'] as const;
export type School = typeof SCHOOL_NAMES[number];
export const SCHOOL_COLORS: Record<School, string> = {
    Arcane: "#62daff",
    Thermal: "#ef4444",
    Kinetic: "#8a8a8a",
    Electric: "#d89911",
    Light: "#fdff79",
    Spatial: "#7c49ce",
    Time: "#90f163",
    Manifest: "#ffffff"
};
